import React, { useMemo, useState, useCallback } from 'react';
import { format } from 'date-fns';
import { StudentAttendance } from '@court-sync/shared';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import {
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Flame,
  ListFilter,
  Download,
  Loader2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  PaginatedTable,
  type PaginationState,
  type PaginatedTableColumn,
} from '../ui/PaginatedTable';
import { useAttendanceExportScope } from '../../hooks/useAttendanceExportScope';
import { studentService } from '../../services/student';
import { downloadCSV } from '../../utils/csvUtils';
import { buildSingleStudentAttendanceCsvRows } from '../../utils/attendanceCsvExport';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';

const HISTORY_PAGE_SIZE = 8;

type StatusFilter = 'all' | 'attended' | 'missed' | 'cancelled' | 'scheduled';

interface AttendanceSummaryProps {
  attendance: StudentAttendance | StudentAttendance[];
  showDetails?: boolean;
  /** lessonId → display name from enrollments API */
  lessonNameById?: Record<string, string>;
  className?: string;
  /** When set, owner/coach can export CSV with optional term/lesson scope. */
  attendanceExport?: {
    studentId: string;
    academyId: string;
    studentFirstName?: string;
    studentLastName?: string;
  };
}

interface AttendanceBarProps {
  percentage: number;
  className?: string;
}

const AttendanceBar: React.FC<AttendanceBarProps> = ({ percentage, className }) => {
  const getBarColor = (rate: number): string => {
    if (rate >= 90) return 'bg-green-500';
    if (rate >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className={cn('w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2', className)}>
      <div
        className={cn('h-2 rounded-full transition-all duration-500', getBarColor(percentage))}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
};

const getAttendanceStatus = (
  rate: number
): { label: string; variant: 'success' | 'warning' | 'destructive' } => {
  if (rate >= 90) return { label: 'Excellent', variant: 'success' };
  if (rate >= 70) return { label: 'Good', variant: 'warning' };
  return { label: 'Needs Attention', variant: 'destructive' };
};

function getSessionStatus(a: StudentAttendance): 'scheduled' | 'attended' | 'missed' | 'cancelled' {
  if (a.sessionStatus) return a.sessionStatus;
  if (a.lessonsCancelled > 0) return 'cancelled';
  if (a.lessonsMissed > 0) return 'missed';
  if (a.lessonsAttended > 0) return 'attended';
  return 'scheduled';
}

function sortTimestamp(row: StudentAttendance): number {
  const raw = row.lessonDate ?? row.lastAttendedDate ?? row.updatedAt ?? row.createdAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function statusBadgeClasses(
  status: ReturnType<typeof getSessionStatus>
): { label: string; className: string } {
  switch (status) {
    case 'attended':
      return {
        label: 'Attended',
        className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
      };
    case 'missed':
      return {
        label: 'Missed',
        className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        className: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
      };
    default:
      return {
        label: 'Scheduled',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      };
  }
}

export const AttendanceSummary: React.FC<AttendanceSummaryProps> = ({
  attendance,
  showDetails = true,
  lessonNameById,
  className,
  attendanceExport,
}) => {
  const attendanceArray = useMemo(
    () => (Array.isArray(attendance) ? attendance : [attendance]),
    [attendance]
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [historyPage, setHistoryPage] = useState(1);
  const [exportingCsv, setExportingCsv] = useState(false);
  const exportScopeState = useAttendanceExportScope(attendanceExport?.academyId);

  const totals = attendanceArray.reduce(
    (acc, a) => ({
      lessonsTotal: acc.lessonsTotal + a.lessonsTotal,
      lessonsAttended: acc.lessonsAttended + a.lessonsAttended,
      lessonsMissed: acc.lessonsMissed + a.lessonsMissed,
      lessonsCancelled: acc.lessonsCancelled + a.lessonsCancelled,
    }),
    { lessonsTotal: 0, lessonsAttended: 0, lessonsMissed: 0, lessonsCancelled: 0 }
  );

  const attendanceRate =
    totals.lessonsTotal > 0
      ? Math.round((totals.lessonsAttended / totals.lessonsTotal) * 100)
      : 0;

  const bestStreak = Math.max(...attendanceArray.map((a) => a.streak || 0), 0);
  const status = getAttendanceStatus(attendanceRate);

  const filteredHistory = useMemo(() => {
    let rows = [...attendanceArray];
    rows.sort((a, b) => sortTimestamp(b) - sortTimestamp(a));
    if (statusFilter !== 'all') {
      rows = rows.filter((r) => getSessionStatus(r) === statusFilter);
    }
    return rows;
  }, [attendanceArray, statusFilter]);

  const historyPagination: PaginationState | null = useMemo(() => {
    const total = filteredHistory.length;
    const totalPages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE) || 1);
    const page = Math.min(Math.max(1, historyPage), totalPages);
    return {
      page,
      limit: HISTORY_PAGE_SIZE,
      total,
      totalPages,
    };
  }, [filteredHistory, historyPage]);

  const historyPageRows = useMemo(() => {
    const page = historyPagination?.page ?? 1;
    const start = (page - 1) * HISTORY_PAGE_SIZE;
    return filteredHistory.slice(start, start + HISTORY_PAGE_SIZE);
  }, [filteredHistory, historyPagination?.page]);

  const historyColumns: PaginatedTableColumn<StudentAttendance>[] = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        render: (row) => {
          const d = row.lessonDate ?? row.lastAttendedDate;
          return (
            <span className="tabular-nums whitespace-nowrap">
              {d ? format(new Date(d), 'MMM d, yyyy') : '—'}
            </span>
          );
        },
      },
      {
        key: 'lesson',
        header: 'Lesson',
        render: (row) => (
          <span className="min-w-0 max-w-[12rem] truncate block" title={lessonNameById?.[row.lessonId]}>
            {lessonNameById?.[row.lessonId] ?? (
              <span className="text-gray-500" title={row.lessonId}>{`Lesson · ${row.lessonId.slice(0, 8)}…`}</span>
            )}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => {
          const sb = statusBadgeClasses(getSessionStatus(row));
          return (
            <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${sb.className}`}>
              {sb.label}
            </span>
          );
        },
      },
      {
        key: 'notes',
        header: 'Notes',
        render: (row) => (
          <span className="text-gray-600 dark:text-gray-400 max-w-[10rem] truncate block" title={row.notes}>
            {row.notes?.trim() ? row.notes : '—'}
          </span>
        ),
      },
    ],
    [lessonNameById]
  );

  const handleExportAttendanceCsv = useCallback(async () => {
    if (!attendanceExport) return;
    setExportingCsv(true);
    try {
      const res = await studentService.getStudentAttendance(
        attendanceExport.studentId,
        attendanceExport.academyId,
        exportScopeState.scope
      );
      const rows = res.data ?? [];
      if (rows.length === 0) {
        toast.error('No attendance records for this scope.');
        return;
      }
      const csvRows = buildSingleStudentAttendanceCsvRows(rows, lessonNameById);
      const label =
        [attendanceExport.studentFirstName, attendanceExport.studentLastName]
          .filter(Boolean)
          .join('-')
          .replace(/\s+/g, '-') || 'student';
      downloadCSV(`attendance-${label}-${new Date().toISOString().split('T')[0]}.csv`, csvRows);
      toast.success(`Exported ${rows.length} session${rows.length !== 1 ? 's' : ''}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportingCsv(false);
    }
  }, [attendanceExport, exportScopeState.scope, lessonNameById]);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-4 h-4 shrink-0" />
            Attendance Summary
          </h3>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="text-center mb-4">
          <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1.5">{attendanceRate}%</div>
          <AttendanceBar percentage={attendanceRate} className="mb-1.5 max-w-xs mx-auto" />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {totals.lessonsAttended} of {totals.lessonsTotal} lessons attended
          </p>
        </div>

        {showDetails && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-md p-2 text-center">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mx-auto mb-0.5" />
              <div className="text-lg font-semibold text-green-600 dark:text-green-400">{totals.lessonsAttended}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Attended</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-md p-2 text-center">
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 mx-auto mb-0.5" />
              <div className="text-lg font-semibold text-red-600 dark:text-red-400">{totals.lessonsMissed}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Missed</div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-md p-2 text-center">
              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mx-auto mb-0.5" />
              <div className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                {totals.lessonsCancelled}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Cancelled</div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-md p-2 text-center">
              <Flame className="w-4 h-4 text-orange-600 dark:text-orange-400 mx-auto mb-0.5" />
              <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">{bestStreak}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">Streak</div>
            </div>
          </div>
        )}

        {showDetails && attendanceArray.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <ListFilter className="w-4 h-4" />
                History
              </h4>
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="sr-only">Filter by status</span>
                <select
                  aria-label="Filter attendance by status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as StatusFilter);
                    setHistoryPage(1);
                  }}
                  className="min-w-[9.5rem] rounded-md border border-input bg-background py-1.5 pl-2.5 pr-10 text-xs text-gray-900 dark:text-gray-100"
                >
                  <option value="all">Newest · All statuses</option>
                  <option value="attended">Attended</option>
                  <option value="missed">Missed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </label>
            </div>

            {attendanceExport && (
              <div className="flex flex-wrap items-end gap-2 rounded-md border border-input bg-muted/40 dark:bg-muted/20 p-2">
                <div className="flex min-w-[10rem] flex-col gap-0.5">
                  <span className="text-[10px] font-medium text-muted-foreground">Term (optional)</span>
                  <select
                    aria-label="Filter export by term"
                    value={exportScopeState.termId}
                    onChange={(e) => {
                      exportScopeState.setTermId(e.target.value);
                    }}
                    disabled={exportScopeState.loading}
                    className="rounded-md border border-input bg-background py-1.5 pl-2 pr-8 text-xs text-gray-900 dark:text-gray-100"
                  >
                    <option value="">All terms</option>
                    {exportScopeState.terms.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex min-w-[12rem] flex-1 flex-col gap-0.5">
                  <span className="text-[10px] font-medium text-muted-foreground">Lesson or camp (optional)</span>
                  <select
                    aria-label="Filter export by lesson or camp"
                    value={exportScopeState.lessonId}
                    onChange={(e) => exportScopeState.setLessonId(e.target.value)}
                    disabled={exportScopeState.loading}
                    className="w-full rounded-md border border-input bg-background py-1.5 pl-2 pr-8 text-xs text-gray-900 dark:text-gray-100 max-w-md"
                  >
                    <option value="">All in academy</option>
                    {exportScopeState.filteredLessons.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({l.lessonType})
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  onClick={() => void handleExportAttendanceCsv()}
                  disabled={exportingCsv || exportScopeState.loading}
                >
                  {exportingCsv ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Download className="h-3.5 w-3.5" aria-hidden />
                  )}
                  Export CSV
                </Button>
              </div>
            )}

            <PaginatedTable
              columns={historyColumns}
              data={historyPageRows}
              pagination={historyPagination}
              emptyMessage="No attendance records match this filter."
              onPageChange={setHistoryPage}
              getRowKey={(row) => row.id}
              loading={false}
              noWrapperPadding
              className="text-xs [&_table]:text-xs [&_th]:py-1.5 [&_td]:py-1.5"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttendanceSummary;
